import React, { useEffect, useState } from "react";
import instance from "../../instance";
import { useNavigate } from "react-router-dom";
import CustomerSearch from "../Customers/CustomerSearch";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const UserLogs = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sortConfig, setSortConfig] = useState({
    key: "LoginTime",
    direction: "descending",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [excludeTerm, setExcludeTerm] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    instance
      .get("users/logs")
      .then((response) => {
        setData(response.data);
        setLoading(false);
      })
      .catch((error) => {
        setError(error.message);
        setLoading(false);
        toast.error("Error fetching logs: " + error.message);
      });
  }, []);

  const handleSort = (column) => {
    const direction =
      sortConfig.direction === "ascending" ? "descending" : "ascending";
    setSortConfig({ key: column, direction });
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleExcludeSearch = (event) => {
    setExcludeTerm(event.target.value);
  };

  const resetSearch = () => {
    setSearchTerm("");
    setExcludeTerm("");
  };

  const getAllUniqueValues = () => {
    const uniqueValues = new Set();
    data.forEach((item) => {
      Object.values(item).forEach((value) => {
        if (value) {
          uniqueValues.add(String(value));
        }
      });
    });
    return Array.from(uniqueValues).sort((a, b) => (a > b ? -1 : 1));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);

    const options = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    };

    return date.toLocaleDateString("en-GB", options);
  };

  const sortedData = React.useMemo(() => {
    let sorted = [...data];

    // Filtering logic
    sorted = sorted.filter((item) => {
      const searchTermLower = searchTerm.toLowerCase();
      const excludeTermLower = excludeTerm.toLowerCase();

      const itemValues = [
        item.Username,
        formatDate(item.LoginTime),
        formatDate(item.LogoutTime),
      ].map((value) => (value ? value.toLowerCase() : ""));

      const matchesSearchTerm =
        searchTerm === "" ||
        itemValues.some((value) => value.includes(searchTermLower));
      const matchesExcludeTerm =
        excludeTerm === "" ||
        !itemValues.some((value) => value.includes(excludeTermLower));

      return matchesSearchTerm && matchesExcludeTerm;
    });

    // Sorting logic
    sorted.sort((a, b) => {
      if (["LoginTime", "LogoutTime"].includes(sortConfig.key)) {
        const dateA = a[sortConfig.key]
          ? new Date(a[sortConfig.key])
          : new Date(0);
        const dateB = b[sortConfig.key]
          ? new Date(b[sortConfig.key])
          : new Date(0);

        if (dateA < dateB) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (dateA > dateB) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      } else {
        const valueA = a[sortConfig.key] || "";
        const valueB = b[sortConfig.key] || "";

        if (valueA < valueB) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (valueA > valueB) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      }
    });

    return sorted.map((item, index) => ({ ...item, sequence: index + 1 }));
  }, [data, sortConfig, searchTerm, excludeTerm]);

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div>
      <header>
        <h1>User Logs</h1>
      </header>
      <div style={{ textAlign: "center" }}>
        <button onClick={() => navigate("/")} className="button">
          ← Main
        </button>
      </div>

      <CustomerSearch
        searchTerm={searchTerm}
        excludeTerm={excludeTerm}
        datalistData={getAllUniqueValues()}
        handleSearch={handleSearch}
        handleExcludeSearch={handleExcludeSearch}
        resetSearch={resetSearch}
        dateFilter={null}
        formatDateRange={() => ""}
      />
      <table id="user-logs-table">
        <thead>
          <tr>
            <th onClick={() => handleSort("sequence")}>#</th>

            <th className="sortable" onClick={() => handleSort("Username")}>
              User Name
            </th>
            <th className="sortable" onClick={() => handleSort("LoginTime")}>
              Login Time
            </th>
            <th className="sortable" onClick={() => handleSort("LogoutTime")}>
              Logout Time
            </th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="4">Loading...</td>
            </tr>
          ) : sortedData.length === 0 ? (
            <tr>
              <td colSpan="4">No logs available</td>
            </tr>
          ) : (
            sortedData.map((item, index) => (
              <tr key={index}>
                <td>{item.sequence}</td>
                <td>{item.Username}</td>
                <td>{formatDate(item.LoginTime)}</td>
                <td>{formatDate(item.LogoutTime)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <footer>
        <span>© 2025 OwlTech All rights reserved.</span>
      </footer>
    </div>
  );
};

export default UserLogs;
